/**
 * SaasVendorContext — active vendor for the unified /saas-ai module.
 *
 * Reads the vendor from the ?vendor= URL query param (shareable / bookmarkable),
 * falls back to the last selection (vendorStore) so internal navigations that
 * drop the param don't reset the view, and syncs vendorStore so saasAiClient
 * queries the right backend. `availableVendors` is gated by the org's module
 * entitlements (from the login user.modules), so the selector only offers
 * vendors the org actually has — unified UI, per-vendor access.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  SAAS_VENDORS,
  getActiveVendor,
  isValidVendor,
  setActiveVendor,
  isValidSurface,
  setActiveSurface,
  getActiveSurface,
  getSurfacesForVendor,
  hasVendorSurfaces,
} from '../services/vendor/vendorStore';
import { getModuleLicense } from '../licensing/useModuleLicense';

const SaasVendorContext = createContext(null);

export function SaasVendorProvider({ children }) {
  const [searchParams, setSearchParams] = useSearchParams();

  // The provider switcher always offers every configured vendor (OpenAI,
  // Anthropic, …). It's the primary way to pick a provider, so it's shown
  // regardless of per-vendor module licence; a vendor the org has not bought
  // opens on its lock screen (SaasVendorLockOutlet).
  const availableVendors = useMemo(() => SAAS_VENDORS, []);

  const urlVendor = searchParams.get('vendor');
  const stored = getActiveVendor();
  // With nothing chosen yet, open on a provider the org holds: an unlicensed
  // one is still selectable, but opens on its lock screen (SaasVendorLockOutlet).
  const license = getModuleLicense();
  const firstLicensed = availableVendors.find((v) => license.isLicensed(v.module || v.id));
  const vendor =
    (isValidVendor(urlVendor) && availableVendors.some((v) => v.id === urlVendor) && urlVendor) ||
    (availableVendors.some((v) => v.id === stored) && stored) ||
    firstLicensed?.id ||
    availableVendors[0]?.id ||
    'anthropic';

  // Surface (chat | code | …, or 'all'): applies to any vendor that has
  // surfaces. Like vendor, fall back to the last selection (vendorStore) so
  // internal navigations that drop the ?surface= param don't reset the
  // surface choice. Vendors without surfaces always resolve to 'all'.
  const urlSurface = searchParams.get('surface');
  const storedSurface = getActiveSurface();
  const surface =
    hasVendorSurfaces(vendor)
      ? ((isValidSurface(urlSurface, vendor) && urlSurface)
        || (isValidSurface(storedSurface, vendor) && storedSurface)
        || 'all')
      : 'all';

  // Keep the module-level store (read by saasAiClient) in sync.
  useEffect(() => {
    setActiveVendor(vendor);
  }, [vendor]);
  useEffect(() => {
    setActiveSurface(surface);
  }, [surface]);

  const setVendor = useCallback(
    (v) => {
      if (!isValidVendor(v)) return;
      setActiveVendor(v);
      const next = new URLSearchParams(searchParams);
      next.set('vendor', v);
      // Surface only applies when valid for the target vendor — drop it when
      // leaving for a vendor without surfaces (or with a different set).
      if (!isValidSurface(next.get('surface'), v)) next.delete('surface');
      setSearchParams(next, { replace: false });
    },
    [searchParams, setSearchParams],
  );

  const setSurface = useCallback(
    (s) => {
      if (!isValidSurface(s, vendor)) return;
      setActiveSurface(s);
      const next = new URLSearchParams(searchParams);
      if (s === 'all') next.delete('surface');
      else next.set('surface', s);
      setSearchParams(next, { replace: false });
    },
    [searchParams, setSearchParams, vendor],
  );

  const value = useMemo(
    () => ({ vendor, setVendor, availableVendors, surface, setSurface, surfaces: getSurfacesForVendor(vendor) }),
    [vendor, setVendor, availableVendors, surface, setSurface],
  );

  return <SaasVendorContext.Provider value={value}>{children}</SaasVendorContext.Provider>;
}

export function useSaasVendor() {
  const ctx = useContext(SaasVendorContext);
  if (!ctx) {
    throw new Error('useSaasVendor must be used within a SaasVendorProvider');
  }
  return ctx;
}
